export enum SoundEffect {
  // UI Sounds
  BUTTON_CLICK = 'button_click',
  BUTTON_HOVER = 'button_hover',
  UI_OPEN = 'ui_open',
  UI_CLOSE = 'ui_close',
  
  // Tower Sounds
  TOWER_PLACE = 'tower_place',
  TOWER_UPGRADE = 'tower_upgrade',
  TOWER_ATTACK = 'tower_attack',
  TOWER_CRIT = 'tower_crit',
  TOWER_SHOOT = 'tower_shoot',
  // Monster Sounds
  MONSTER_SPAWN = 'monster_spawn',
  MONSTER_DEATH = 'monster_death',
  MONSTER_HIT = 'monster_hit',
  BOSS_SPAWN = 'boss_spawn',
  BOSS_DEATH = 'boss_death',
  
  // Earth Sounds
  EARTH_DAMAGE = 'earth_damage',
  EARTH_ABILITY = 'earth_ability',
  EARTH_HEAL = 'earth_heal',
  EARTH_UPGRADE = 'earth_upgrade',
  
  // Game Events
  LEVEL_UP = 'level_up',
  GAME_OVER = 'game_over',
  VICTORY = 'victory',
  
  // Equipment Sounds
  EQUIPMENT_BUY = 'equipment_buy',
  EQUIPMENT_UPGRADE = 'equipment_upgrade',
  EQUIPMENT_COMBINE = 'equipment_combine',

  // Pet Sounds
  PET_SUMMON = 'pet_summon',
  PET_UPGRADE = 'pet_upgrade',
  PET_DEATH = 'pet_death',
  PET_HIT = 'pet_hit',
  PET_CRIT = 'pet_crit',
  PET_HEAL = 'pet_heal',

  // Ad Sounds
  AD_REWARD = 'ad_reward',
  AD_ERROR = 'ad_error',
  AD_LOADING = 'ad_loading',
  AD_OPEN = 'ad_open',
  AD_CLOSE = 'ad_close',
  AD_SKIP = 'ad_skip',

  // Item Sounds
  ITEM_UNLOCK = 'item_unlock',
  ITEM_EQUIP = 'item_equip',
  ITEM_UNEQUIP = 'item_unequip',
  ITEM_SELL = 'item_sell',
  ITEM_BUY = 'item_buy',
  ITEM_UPGRADE = 'item_upgrade',


}

export enum BackgroundMusic {
  MAIN_MENU = 'bg_main_menu',
  GAMEPLAY = 'bg_gameplay',
  BOSS_FIGHT = 'bg_boss_fight',
  GAME_OVER = 'bg_game_over',
}

export interface AudioSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
}

export class AudioManager {
  private static instance: AudioManager;
  private scene: Phaser.Scene;
  private sounds: Map<string, Phaser.Sound.BaseSound> = new Map();
  private currentMusic: Phaser.Sound.BaseSound | null = null;
  private settings: AudioSettings;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.settings = this.loadSettings();
    this.initializeAudio();
  }

  public static getInstance(scene?: Phaser.Scene): AudioManager {
    if (!AudioManager.instance && scene) {
      AudioManager.instance = new AudioManager(scene);
    }
    return AudioManager.instance;
  }

  private loadSettings(): AudioSettings {
    const saved = localStorage.getItem('audioSettings');
    if (saved) {
      return JSON.parse(saved);
    }
    
    // Default settings
    return {
      masterVolume: 1.0,
      musicVolume: 0.7,
      sfxVolume: 0.8,
      muted: false,
    };
  }

  private saveSettings(): void {
    localStorage.setItem('audioSettings', JSON.stringify(this.settings));
  }

  private initializeAudio(): void {
    // Initialize sound effects (using generated tones since we don't have audio files)
    this.createGeneratedSounds();
  }

  private createGeneratedSounds(): void {
    // Create simple generated sounds using Web Audio API
    // This is a placeholder - in real game you'd load actual audio files
    
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Helper function to create simple tones
    const createTone = (frequency: number, duration: number, type: OscillatorType = 'sine'): Phaser.Sound.BaseSound => {
      return {
        play: () => {
          if (this.settings.muted) return;
          
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
          oscillator.type = type;
          
          const volume = this.settings.masterVolume * this.settings.sfxVolume * 0.1;
          gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
          
          oscillator.start();
          oscillator.stop(audioContext.currentTime + duration);
        },
        stop: () => {},
        volume: 1.0,
        destroy: () => {},
      } as any;
    };

    // UI Sounds
    this.sounds.set(SoundEffect.BUTTON_CLICK, createTone(800, 0.1, 'square'));
    this.sounds.set(SoundEffect.BUTTON_HOVER, createTone(600, 0.05, 'sine'));
    this.sounds.set(SoundEffect.UI_OPEN, createTone(440, 0.2, 'triangle'));
    this.sounds.set(SoundEffect.UI_CLOSE, createTone(330, 0.15, 'triangle'));
    
    // Tower Sounds
    this.sounds.set(SoundEffect.TOWER_PLACE, createTone(500, 0.3, 'square'));
    this.sounds.set(SoundEffect.TOWER_UPGRADE, createTone(660, 0.4, 'sawtooth'));
    this.sounds.set(SoundEffect.TOWER_ATTACK, createTone(1000, 0.08, 'square'));
    this.sounds.set(SoundEffect.TOWER_CRIT, createTone(1200, 0.12, 'sawtooth'));
    
    // Monster Sounds
    this.sounds.set(SoundEffect.MONSTER_DEATH, createTone(220, 0.3, 'sawtooth'));
    this.sounds.set(SoundEffect.MONSTER_HIT, createTone(150, 0.1, 'square'));
    this.sounds.set(SoundEffect.BOSS_SPAWN, createTone(110, 0.8, 'triangle'));
    this.sounds.set(SoundEffect.BOSS_DEATH, createTone(80, 1.0, 'sawtooth'));
    
    // Earth Sounds
    this.sounds.set(SoundEffect.EARTH_DAMAGE, createTone(200, 0.2, 'triangle'));
    this.sounds.set(SoundEffect.EARTH_ABILITY, createTone(880, 0.5, 'sine'));
    this.sounds.set(SoundEffect.EARTH_HEAL, createTone(740, 0.3, 'triangle'));
    
    // Game Events
    this.sounds.set(SoundEffect.LEVEL_UP, createTone(523, 0.6, 'triangle')); // C5
    this.sounds.set(SoundEffect.GAME_OVER, createTone(165, 1.2, 'sawtooth')); // E3
    this.sounds.set(SoundEffect.VICTORY, createTone(659, 0.8, 'sine')); // E5
    
    // Equipment Sounds
    this.sounds.set(SoundEffect.EQUIPMENT_BUY, createTone(784, 0.3, 'triangle')); // G5
    this.sounds.set(SoundEffect.EQUIPMENT_UPGRADE, createTone(932, 0.4, 'sine')); // A#5
    this.sounds.set(SoundEffect.EQUIPMENT_COMBINE, createTone(1047, 0.6, 'triangle')); // C6
  }

  // Play sound effect
  public playSFX(effect: SoundEffect, volume: number = 1.0): void {
    if (this.settings.muted) return;
    
    const sound = this.sounds.get(effect);
    if (sound) {
      const finalVolume = this.settings.masterVolume * this.settings.sfxVolume * volume;
      // Use volume property instead of setVolume method
      (sound as any).volume = finalVolume;
      sound.play();
    }
  }

  // Play background music
  public playMusic(music: BackgroundMusic, loop: boolean = true, fadeIn: boolean = true): void {
    if (this.settings.muted) return;
    
    // Stop current music
    if (this.currentMusic) {
      if (fadeIn) {
        this.fadeOutMusic(this.currentMusic, 500);
      } else {
        this.currentMusic.stop();
      }
    }
    
    // For now, create simple ambient tones for background music
    this.createBackgroundMusic(music, loop, fadeIn);
  }

  private createBackgroundMusic(music: BackgroundMusic, loop: boolean, fadeIn: boolean): void {
    // Create simple ambient background music using oscillators
    // In a real game, you'd load actual music files
    
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    let frequency = 220; // Base frequency
    
    switch (music) {
      case BackgroundMusic.MAIN_MENU:
        frequency = 330; // E4
        break;
      case BackgroundMusic.GAMEPLAY:
        frequency = 220; // A3
        break;
      case BackgroundMusic.BOSS_FIGHT:
        frequency = 185; // F#3
        break;
      case BackgroundMusic.GAME_OVER:
        frequency = 165; // E3
        break;
    }
    
    // Create a simple ambient pad
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const filter = audioContext.createBiquadFilter();
    
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = 'sine';
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, audioContext.currentTime);
    
    const volume = this.settings.masterVolume * this.settings.musicVolume * 0.05;
    
    if (fadeIn) {
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 2);
    } else {
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    }
    
    oscillator.start();
    
    // Store reference for stopping later
    this.currentMusic = {
      stop: () => {
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.stop(audioContext.currentTime + 0.5);
      },
      volume: volume,
      destroy: () => oscillator.stop(),
    } as any;
  }

  private fadeOutMusic(music: Phaser.Sound.BaseSound, duration: number): void {
    if (music) {
      // Simple fade out using volume property
      const startVolume = (music as any).volume || 1.0;
      const fadeSteps = 10;
      const stepDuration = duration / fadeSteps;
      
      for (let i = 1; i <= fadeSteps; i++) {
        setTimeout(() => {
          const newVolume = startVolume * (1 - i / fadeSteps);
          (music as any).volume = Math.max(0, newVolume);
          
          if (i === fadeSteps) {
            music.stop();
          }
        }, stepDuration * i);
      }
    }
  }

  // Stop all sounds
  public stopAll(): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
    
    this.sounds.forEach(sound => {
      if (sound.stop) sound.stop();
    });
  }

  // Volume controls
  public setMasterVolume(volume: number): void {
    this.settings.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateMusicVolume();
    this.saveSettings();
  }

  public setMusicVolume(volume: number): void {
    this.settings.musicVolume = Math.max(0, Math.min(1, volume));
    this.updateMusicVolume();
    this.saveSettings();
  }

  public setSFXVolume(volume: number): void {
    this.settings.sfxVolume = Math.max(0, Math.min(1, volume));
    this.saveSettings();
  }

  public setMuted(muted: boolean): void {
    this.settings.muted = muted;
    if (muted) {
      this.stopAll();
    }
    this.saveSettings();
  }

  private updateMusicVolume(): void {
    if (!this.currentMusic) return;
    const volume = this.settings.masterVolume * this.settings.musicVolume;
    (this.currentMusic as any).volume = volume;
  }

  // Getters
  public getSettings(): AudioSettings {
    return { ...this.settings };
  }

  public isMuted(): boolean {
    return this.settings.muted;
  }

  // Cleanup
  public destroy(): void {
    this.stopAll();
    this.sounds.clear();
    AudioManager.instance = null as any;
  }

  // Quick access methods for common sounds
  public playButtonClick(): void {
    this.playSFX(SoundEffect.BUTTON_CLICK);
  }

  public playButtonHover(): void {
    this.playSFX(SoundEffect.BUTTON_HOVER);
  }

  public playTowerAttack(isCrit: boolean = false): void {
    this.playSFX(isCrit ? SoundEffect.TOWER_CRIT : SoundEffect.TOWER_ATTACK);
  }

  public playMonsterDeath(): void {
    this.playSFX(SoundEffect.MONSTER_DEATH);
  }

  public playLevelUp(): void {
    this.playSFX(SoundEffect.LEVEL_UP);
  }

  public playEarthAbility(): void {
    this.playSFX(SoundEffect.EARTH_ABILITY);
  }
} 